import { Stack, useRouter } from 'expo-router';
import useGetAdvanceScreenContent from 'features/advance/hooks/useGetAdvanceScreenContent';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import { NavigationBar } from 'shared/components/vxl/navigation/NavigationBar/NavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import useDefaultError from 'shared/hooks/errorFormatters/useDefaultError/useDefaultError';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import EmptyStateScreen from 'shared/patterns/vxl/EmptyStateScreen/EmptyStateScreen';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

const Throttled = () => {
  const router = useRouter();
  const {
    result: advanceScreenContent,
    loading: advanceScreenContentLoading,
    errorModel: advanceScreenContentErrorModel,
  } = useGetAdvanceScreenContent();

  const throttlingScreenContent = advanceScreenContent?.throttledScreenContent;
  const { t } = useTranslation('advance', {
    keyPrefix: 'throttling',
  });

  const defaultError = useDefaultError();
  const dismissToTab = useDismissToRootTab();

  const handleGoHome = () => {
    dismissToTab(Tabs.HOME);
  };

  const handleBackPress = () => {
    if (router.canGoBack()) {
      router.back();
    } else {
      router.replace('/');
    }
  };

  const handleSettingsPress = () => {
    router.push({
      pathname: '/accounts/advance-details',
    });
  };

  const isOverlayLoading = advanceScreenContentLoading
    ? !advanceScreenContentErrorModel
    : false;
  const shouldShowContent = !advanceScreenContentLoading
    ? !advanceScreenContentErrorModel
    : false;

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          header: () => (
            <NavigationBar
              title={t('title')}
              primaryIcon={
                <Icon
                  iconKey="BACK"
                  iconSize="MD"
                  iconColor={theme.color.text.primary}
                  iconAccessibilityLabel="back-icon"
                />
              }
              primaryOnPress={handleBackPress}
              trailingAccessories={
                <IconButton
                  iconKey="SETTINGS"
                  iconSize="MD"
                  iconButtonAccessibilityLabel="settings"
                  iconOnPress={handleSettingsPress}
                />
              }
            />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={isOverlayLoading}
        LoadingComponent={<FullScreenLoading isCoinLoader />}
        ErrorComponent={
          advanceScreenContentErrorModel ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={defaultError.title}
              messageBody={defaultError.message}
              primaryButton={{
                title: t('errorButton'),
                onPress: handleGoHome,
              }}
            />
          ) : null
        }
      />
      {shouldShowContent ? (
        <View style={styles.emptyStateScreenView}>
          <EmptyStateScreen
            headlineText={throttlingScreenContent?.title ?? ''}
            illustrationKey="HOURGLASS_A"
            content={
              <BodyBaseText numberOfLines={0} style={styles.bodyTextContainer}>
                {throttlingScreenContent?.description ?? ''}
              </BodyBaseText>
            }
            actionButtonText={t('buttonGotIt')}
            actionOnPress={handleGoHome}
          />
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  emptyStateScreenView: {
    flexGrow: 1,
    paddingHorizontal: theme.spacing.m,
  },
  bodyTextContainer: {
    marginTop: theme.spacing.s,
    textAlign: 'center',
  },
});

export default Throttled;
